import {searchUrl} from '@github-ui/issue-url-helper'
import {LABELS} from '@github-ui/issues-shared/labels'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import type {ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {MilestonePicker} from '@github-ui/item-picker-milestone-tsq'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {MilestoneIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {TEST_IDS} from '../../../constants/test-ids'
import {useActiveSearchQuery, useCurrentViewId, useQueryDebouncedSearch} from '../../../contexts/QueryContext'
import {getTokensByType, replaceAllFiltersByTypeInSearchQuery} from '../../../utils/urls'
import type {FilterBarPickerProps} from './ListItemsHeaderWithoutBulkActions'
import styles from './ListMilestoneFilter.module.css'
import {useActiveMilestone} from './use-active-milestone'

export const ListMilestoneFilter = ({nested, repo, applySectionFilter}: FilterBarPickerProps) => {
  const currentViewId = useCurrentViewId()
  const activeSearchQuery = useActiveSearchQuery()
  const {debouncedDirtySearchQuery} = useQueryDebouncedSearch()

  const query = debouncedDirtySearchQuery ?? activeSearchQuery

  const NoMilestoneItem = useMemo(() => {
    const option: ExtendedItemProps<MilestonePickerMilestone> = {
      id: SPECIAL_VALUES.noMilestoneData.id,
      description: '',
      descriptionVariant: 'inline',
      /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
      children: <VerifiedHTMLText html={SPECIAL_VALUES.noMilestoneData.title as SafeHTMLString} />,
      source: SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestone,
      groupId: '',
      selected: query.includes('no:milestone'),
      leadingVisual: () => <MilestoneIcon />,
    }
    return option
  }, [query])

  // Check if there are any milestone tokens in the query, we only support one selection at this time
  // so default to the first one. Without updates to the filter, we cannot prevent the user from doing this manually.
  const currentMilestoneTokens = useMemo(() => {
    const tokens = getTokensByType(query, 'milestone')
    return tokens
  }, [query])

  // Active milestone clears when the query loses its milestone token.
  const [activeMilestone, setActiveMilestone] = useActiveMilestone(currentMilestoneTokens)

  const onSelectionChanged = useCallback(
    (selectedMilestones: MilestonePickerMilestone[]) => {
      const isNegatedFilter = selectedMilestones[0]?.id === SPECIAL_VALUES.noMilestoneData.id
      const milestones = selectedMilestones.map(milestone => milestone.title)

      if (selectedMilestones[0]) {
        setActiveMilestone(selectedMilestones[0])
      }

      const newQuery = replaceAllFiltersByTypeInSearchQuery(query, milestones, 'milestone', isNegatedFilter)
      const url = searchUrl({viewId: currentViewId, query: newQuery})
      applySectionFilter(newQuery, url)
    },
    [query, applySectionFilter, currentViewId, setActiveMilestone],
  )

  return (
    <MilestonePicker
      repo={repo.name}
      activeMilestone={activeMilestone}
      anchorElement={nested ? NestedMilestonesAnchor : MilestonesAnchor}
      owner={repo.owner}
      onSelectionChange={onSelectionChanged}
      noMilestoneItem={NoMilestoneItem}
      title={LABELS.filters.milestonesLabel}
      canCreateMilestone={false}
    />
  )
}

function NestedMilestonesAnchor(props: React.HTMLAttributes<HTMLElement>) {
  return (
    <ActionList.Item {...props} aria-label={LABELS.filters.milestonesLabel} role="menuitem">
      <ActionList.LeadingVisual>
        <MilestoneIcon />
      </ActionList.LeadingVisual>
      {LABELS.filters.milestones}...
    </ActionList.Item>
  )
}

function MilestonesAnchor(props: React.HTMLAttributes<HTMLButtonElement>) {
  return (
    <Button
      variant="invisible"
      data-testid={TEST_IDS.milestoneAnchorFilter}
      trailingVisual={TriangleDownIcon}
      aria-label={LABELS.filters.milestonesLabel}
      className={styles.milestoneFilterButton}
      {...props}
    >
      {LABELS.filters.milestones}
    </Button>
  )
}
